import React, { useState } from 'react';

import {
  CmButton,
  CmFormErrorMessage,
  CmLabelAndInput,
  CmLongLoading,
  CmModal,
  CmModalBody,
  CmModalFooter,
  SelectForm,
} from '@/components/uiParts';
import { myObjectKeys } from '@/global/function';
import { errorHandler } from '@/lib/ErrorHandler';
import { HttpError } from '@/lib/exception';
import { authStoreForm } from '@/pages/store-management/auth-stores/service';
import { useStoreManagementAssociateBrandCreate } from '@/services/StoreManagement/Brands/hooks';
import { FormError, validBlank, validMaxStr } from '@/validate';
import { validNum } from '@/validate/number';

const { MAX_255 } = validNum;

interface Props {
  IsOpenCreateAssociateBrandModal: boolean;
  addCreatedAssociateBrandToSelectBox: (authStore: SelectForm) => void;
  handleCloseCreateAssociateBrandModal: () => void;
}

type AssociateBrandFormError = {
  name: FormError;
};

const validateName = (
  name: string,
  error: AssociateBrandFormError
): AssociateBrandFormError => {
  error.name.isTouched = true;
  const label = authStoreForm.associateBrand.label;
  const isMax = validMaxStr.check(name, MAX_255);
  const isBlank = validBlank.check(name);
  if (isBlank) error.name.message = validBlank.message(label);
  if (isMax) error.name.message = validMaxStr.message(label, MAX_255);
  error.name.isError = isBlank || isMax;
  return error;
};

export const CreateAssociateBrandModal: React.FC<Props> = ({
  IsOpenCreateAssociateBrandModal,
  addCreatedAssociateBrandToSelectBox,
  handleCloseCreateAssociateBrandModal,
}) => {
  const errorObj = { message: '', isError: false, isTouched: false };
  const initialErrorObj = {
    name: { ...errorObj },
  };
  const [error, setError] = useState<AssociateBrandFormError>({
    ...initialErrorObj,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');

  const isDisabledCreateButton = [
    error.name.isError || !error.name.isTouched,
  ].some((isError) => isError);

  const handleInputName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    const newError = validateName(newName, error);
    setName(newName), setError(newError);
  };

  const initializeForm = () => {
    setName('');
    setError({ ...initialErrorObj });
  };

  const handleInitializeModal = () => {
    initializeForm();
    handleCloseCreateAssociateBrandModal();
  };

  const handleCreateAssociateBrand = async () => {
    // 最終確認のバリデーション
    const newError = { ...error };
    myObjectKeys(newError).forEach((key) => (newError[key].isTouched = true));
    setError(newError);
    const isError = [validateName(name, error).name.isError].some((err) => err);
    if (isError) {
      return alert(
        '不正な入力内容が確認されました。入力内容をご確認ください。'
      );
    }
    setIsLoading(true);
    try {
      const associateBrand = await useStoreManagementAssociateBrandCreate({
        name,
      });
      addCreatedAssociateBrandToSelectBox({
        value: associateBrand.id.toString(),
        label: associateBrand.name,
      });
      alert('ブランドを登録しました。');
      handleInitializeModal();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      if (error instanceof HttpError && error.status === 400) {
        return alert(error.message);
      }
      // @ts-ignore TS2322
      errorHandler({ error });
    }
  };

  return (
    <CmModal
      onClose={handleInitializeModal}
      {...{ isOpen: IsOpenCreateAssociateBrandModal }}
      title="ブランド登録"
    >
      <CmModalBody pb="10">
        <CmLabelAndInput
          isRequired
          id={'modal-' + authStoreForm.associateBrand}
          isInvalid={error.name.isError}
          label={authStoreForm.associateBrand.label}
          fontWeight="bold"
          value={name}
          onChange={handleInputName}
        >
          <CmFormErrorMessage>{error.name.message}</CmFormErrorMessage>
        </CmLabelAndInput>
      </CmModalBody>
      <CmModalFooter>
        {/*
         // @ts-ignore TS2590 */}
        <CmButton
          onClick={handleCreateAssociateBrand}
          isDisabled={isDisabledCreateButton}
        >
          登録
        </CmButton>
        <CmButton ml="3" variant="outline" onClick={handleInitializeModal}>
          閉じる
        </CmButton>
        {isLoading && <CmLongLoading />}
      </CmModalFooter>
    </CmModal>
  );
};
